import { makeBookId } from '../Contexts/DiffitBooksContext';
import { getSignedURL, uploadToS3 } from '../../../services/AWSService';
import { createPacket } from '../../../services/PacketService';
import {
  LITERALLY_ANYTHING_ID,
  VOCABULARY_ID,
  TEXT_OR_LINK_ID,
  BOOKS_ID,
  getVocabWordListFromString,
  TABS,
} from '../Contexts/PacketInputContext';
import { isValidUrl } from '../Tabs/TextOrLinkInput';
import { isURL } from 'validator';
import axios from 'axios';

const VALIDATION_ERRORS = {
  NO_TOPIC: { error: 'NoTopic', title: 'Please enter a topic' },
  NO_VOCABULARY: { error: 'NoVocabulary', title: 'Please enter a vocabulary list' },
  TOO_MANY_WORDS: {
    error: 'TooManyVocabWords',
    title: 'Sorry, that’s too many words',
    message: 'Diffit can only process up to 20 vocabulary words at a time.',
  },
  NO_BOOK: { error: 'NoBook', title: 'Please select a book' },
  NO_START_CHAPTER: { error: 'NoStartChapter', title: 'Please select a start chapter' },
  NO_END_CHAPTER: { error: 'NoEndChapter', title: 'Please select an end chapter' },
  NO_TEXT: { error: 'NoText', title: 'Please enter some text or upload a PDF' },
  PDF_UPLOAD_FAILED: {
    error: 'PdfUploadFailed',
    title: 'We are still uploading your PDF. Please try again in a few seconds.',
  },
  INVALID_URL: { error: 'InvalidUrl', title: 'Please enter a valid URL' },
  NO_TEXT_OR_LINK: {
    error: 'NoTextOrLink',
    title: 'Please enter some text, upload a PDF, or enter a URL',
  },
};

export function packetInputHasErrors({
  inputType,
  topic,
  book,
  startChapter,
  endChapter,
  url,
  text,

  pdfFile,
  uploadedPdfURL,
  words,
  contentStandard,
}) {
  switch (inputType.id) {
    case TABS[0].id:
      if (!contentStandard && !topic) return VALIDATION_ERRORS.NO_TOPIC;
      return false;
    case TABS[1].id:
      if (!book) return VALIDATION_ERRORS.NO_BOOK;
      if (!startChapter || startChapter === -1) return VALIDATION_ERRORS.NO_START_CHAPTER;
      if (!endChapter || endChapter === -1) return VALIDATION_ERRORS.NO_END_CHAPTER;
      return false;
    case TABS[2].id:
      // For the combined text or link input
      if (!text && !url && !pdfFile) return VALIDATION_ERRORS.NO_TEXT_OR_LINK;
      if (url && !isValidUrl(url)) return VALIDATION_ERRORS.INVALID_URL;
      if (pdfFile && !uploadedPdfURL) {
        return VALIDATION_ERRORS.PDF_UPLOAD_FAILED;
      }
      return false;
    case TABS[3].id:
      if (!words) return VALIDATION_ERRORS.NO_VOCABULARY;
      if (getVocabWordListFromString(words).length > 20) return VALIDATION_ERRORS.TOO_MANY_WORDS;
      return false;
    default:
      return false;
  }
}

export async function generatePacket({
  inputType,
  topic,
  genre,
  url,
  text,
  pdfFile,
  uploadedPdfURL,
  words,
  preserveOriginalText,
  book,
  startChapter,
  endChapter,
  readingLevel,
  language,
  languageStandards,
  contentStandard,
  orientation,
  templateId,
  navigate, // pass in navigate if you want to redirect
}) {
  let error = packetInputHasErrors({
    inputType,
    topic,
    book,
    startChapter,
    endChapter,
    url,
    text,
    pdfFile,
    uploadedPdfURL,
    words,
    contentStandard,
  });
  if (error) {
    throw new Error('Invalid input sent to generatePacket function:', error);
  }
  const payload = {
    input_type: inputType.id,
    genre: 'Nonfiction',
    reading_level: readingLevel,
    language: language,
    language_standards_json: languageStandards,
    content_standards_json: contentStandard,
    template_id: templateId,
    orientation: orientation,
  };

  if (inputType.id === LITERALLY_ANYTHING_ID) {
    payload.topic = topic;
    payload.genre = genre.server_value;
  } else if (inputType.id === BOOKS_ID) {
    payload.startChapterIdx = startChapter.value - 1;
    payload.endChapterIdx = endChapter.value - 1;
    payload.bookId = makeBookId(book);
  } else if (inputType.id === TEXT_OR_LINK_ID) {
    // Determine if this is a URL or text/PDF input
    if (url) {
      payload.url = url;
      payload.input_type = 'url';
    } else if (text) {
      payload.text = text;
      payload.input_type = 'text';
    } else if (pdfFile) {
      payload.uploaded_pdf_url = uploadedPdfURL;
      payload.input_type = 'text';
    }
    payload.keep_original_text = preserveOriginalText;
  } else if (inputType.id === VOCABULARY_ID) {
    payload.vocab_list = getVocabWordListFromString(words);
  }

  try {
    const response = await createPacket(payload, () => {});
    const packetId = response.data.packet_id;

    if (navigate) {
      sessionStorage.setItem('initial_generation', 'true');
      navigate(`/p/${packetId}`);
    }
    return {
      packetUrl: `/p/${packetId}`,
    };
  } catch (err) {
    error = err.response?.data || {
      message: 'Sorry, something went wrong. Please try again.',
    };
    return {
      error: error,
    };
  }
}

export async function uploadPdfToS3(pdfFile) {
  const s3Info = await getSignedURL(pdfFile.name, 'application/pdf');
  await uploadToS3(s3Info.data.signed_url, pdfFile, 'application/pdf');
  return s3Info.data.unsigned_url;
}

export const isYoutubeUrl = (url) => {
  if (!url) return false;

  try {
    const parsedUrl = new URL(url);

    // Check for allowed schemas
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return false;

    // Check for allowed domains
    const allowedDomains = [
      'youtu.be',
      'm.youtube.com',
      'youtube.com',
      'www.youtube.com',
      'www.youtube-nocookie.com',
      'vid.plus',
    ];

    if (!allowedDomains.includes(parsedUrl.hostname)) return false;

    // Check for video ID in different URL formats
    if (parsedUrl.pathname.endsWith('/watch')) {
      return parsedUrl.searchParams.has('v');
    } else {
      const path = parsedUrl.pathname.replace(/^\//, '');
      const videoId = path.split('/').pop();
      return videoId && videoId.length === 11;
    }
  } catch (e) {
    // Invalid URL format
    return false;
  }
};

// A Short (youtube.com/shorts/<id>) rather than a regular video. The Chrome
// extension only reads YouTube on /watch pages — its site rule and its "Diffit
// this video" button both match /watch and youtu.be — so a Short is the one
// YouTube URL we shouldn't point teachers at the extension for.
//
// Only the /shorts/ path is detectable: a youtu.be share link for a Short looks
// exactly like one for a regular video, so it reads as a video here.
export const isYoutubeShortUrl = (url) => {
  if (!isYoutubeUrl(url)) return false;

  try {
    return new URL(url).pathname.startsWith('/shorts/');
  } catch (e) {
    return false;
  }
};

// Common TLDs that are almost certainly real URLs even as bare domains (word.tld).
// Bare domains with uncommon TLDs like .how, .what, .why are rejected to avoid
// false positives when teachers omit spaces after punctuation (e.g. "things.How").
const COMMON_TLDS = new Set([
  'com',
  'org',
  'edu',
  'gov',
  'net',
  'io',
  'co',
  'us',
  'uk',
  'ca',
  'au',
  'me',
  'app',
  'dev',
  'ai',
]);

// Stricter URL check: requires protocol, path, subdomain, or a common TLD
const isLikelyUrl = (token) => {
  if (!isURL(token)) return false;
  if (/^https?:\/\//i.test(token)) return true;
  // A bare dotted number is a standards code ("4.2.4.1"), not a host: isURL accepts the four-part
  // ones as IPv4 addresses, and each was fetched as a source, stalling follow-up for 30 s.
  if (/^\d+(\.\d+)+$/.test(token)) return false;
  if (token.includes('/')) return true;
  const parts = token.split('.');
  if (parts.length - 1 >= 2) return true;
  const tld = parts[parts.length - 1].toLowerCase();
  return COMMON_TLDS.has(tld);
};

// Split on whitespace or sentence boundaries (punctuation followed by uppercase)
// to avoid joining adjacent sentences into false URLs (e.g. "things.How" from "things.How do...")
const splitIntoTokens = (text) => text.split(/\s+|(?<=[.?!])(?=[A-Z])/);

// Above this length the text is material the teacher pasted (a transcript, an article), not a
// request. A bare domain inside it ("visit our site pdf.com") is a mention, not a source to fetch:
// a quiz transcript naming "pdf.com" was fetched as the packet's only web source, and that
// unrelated page became an activity. Links with a protocol, path or subdomain still count.
const PASTED_TEXT_MIN_CHARS = 500;

const isBareDomain = (token) =>
  !/^https?:\/\//i.test(token) && !token.includes('/') && token.split('.').length === 2;

// Extract all non-YouTube URLs found in a text string
export const extractUrlsFromText = (text) => {
  if (!text) return [];
  const isPastedText = text.length > PASTED_TEXT_MIN_CHARS;
  const words = splitIntoTokens(text);
  const urls = [];
  for (const word of words) {
    const cleanWord = word.replace(/[.,;:!?)]+$/, '');
    if (isPastedText && isBareDomain(cleanWord)) continue;
    if (isLikelyUrl(cleanWord) && !isYoutubeUrl(cleanWord)) {
      urls.push(cleanWord);
    }
  }
  return urls;
};

// Extract YouTube URLs found in a text string
export const extractYoutubeUrlsFromText = (text) => {
  if (!text) return [];
  const words = splitIntoTokens(text);
  const urls = [];
  for (const word of words) {
    const cleanWord = word.replace(/[.,;:!?)]+$/, '');
    if (isLikelyUrl(cleanWord) && isYoutubeUrl(cleanWord)) {
      urls.push(cleanWord);
    }
  }
  return urls;
};

const convertGoogleDocsUrlToPDFExportUrl = (url) => {
  const parts = url.split('/');
  parts[parts.length - 1] = 'export?format=pdf';
  return parts.join('/');
};

export const getGoogleDocsPdf = async (url, { onFirstByte } = {}) => {
  try {
    let hasFiredFirstByte = false;
    const response = await axios.get(convertGoogleDocsUrlToPDFExportUrl(url), {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (!hasFiredFirstByte && progressEvent?.loaded > 0) {
          hasFiredFirstByte = true;
          onFirstByte && onFirstByte();
        }
      },
    });

    // Ensure we only proceed on 200 OK
    if (response.status !== 200) {
      return null;
    }

    // Try to parse filename from Content-Disposition header
    const contentDisposition = response.headers?.['content-disposition'] || '';
    let filename = 'document.pdf';
    const filenameMatch = /filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i.exec(
      contentDisposition
    );
    if (filenameMatch) {
      filename = decodeURIComponent(filenameMatch[1] || filenameMatch[2] || filename).replace(
        /\+/g,
        ' '
      );
      if (!filename.toLowerCase().endsWith('.pdf')) {
        filename = `${filename}.pdf`;
      }
    }

    // Create a File from the blob so we can reuse the existing PDF upload flow
    const file = new File([response.data], filename, { type: 'application/pdf' });
    return file;
  } catch (error) {
    return null;
  }
};
